<template>
    <button
        :type="type"
        class="design-button"
        :class="[
            secondary ? 'is-secondary' : `color-${color}`,
            tone ? `tone-${tone}` : '',
            `size-${size}`,
            { 'icon-only': iconOnly, 'hide-label-mobile': hideLabelMobile && !iconOnly, 'full-width': fullWidth }
        ]"
        :style="customStyle"
        :disabled="disabled || loading"
        :title="title || label"
        @click="onClick"
    >
        <span class="button-inner">
            <span class="button-content" :class="{ invisible: loading }">
                <inline-svg v-if="icon" class="button-icon" :class="{ filled: filledIcon }" :src="require(`@/assets/icons/${icon}`)"/>
                <span v-if="!iconOnly && label" class="button-label">{{ label }}</span>
                <slot></slot>
            </span>

            <span v-if="loading" class="button-loader">
                <DesignSpinner/>
            </span>
        </span>
    </button>
</template>

<script>
import DesignSpinner from '@/views/components/DesignSpinner.vue';

export default {
    name: 'DesignButton',
    components: { DesignSpinner },
    props: {
        label: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: ''
        },
        color: {
            type: String,
            default: 'primary'
        },
        secondary: {
            type: Boolean,
            default: false
        },
        tone: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: 'default'
        },
        filledIcon: {
            type: Boolean,
            default: false
        },
        contentColor: {
            type: String,
            default: ''
        },
        backgroundColor: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hideLabel: {
            type: Boolean,
            default: false
        },
        hideLabelMobile: {
            type: Boolean,
            default: true
        },
        fullWidth: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: ''
        },
        type: {
            type: String,
            default: 'button'
        }
    },
    emits: ['click'],
    computed: {
        iconOnly() {
            return this.hideLabel || !this.label;
        },
        customStyle() {
            const style = {};
            if (this.backgroundColor) style.backgroundColor = this.backgroundColor;
            if (this.contentColor) style.color = this.contentColor;
            return style;
        }
    },
    methods: {
        onClick(event) {
            if (this.loading || this.disabled) return;
            this.$emit('click', event);
        }
    }
}
</script>

<style lang="scss" scoped>
.design-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: $control-height;
    min-height: $control-height;
    min-width: $control-height;
    width: auto !important;
    flex: 0 0 auto;
    padding: 0 18px !important;

    &.size-small {
        height: 26px;
        min-height: 26px;
        min-width: 26px;
        padding: 0 8px !important;
        font-size: 11px;

        &.icon-only {
            width: 26px !important;
            min-width: 26px !important;
            max-width: 26px !important;
        }

        .button-icon {
            width: 13px;
            height: 13px;
            min-width: 13px;
        }
    }

    &.size-cell {
        height: $cell-control-height;
        min-height: $cell-control-height;
        min-width: $cell-control-height;
        padding: 0 12px !important;
        font-size: $font-small;

        &.icon-only {
            width: $cell-control-height !important;
            min-width: $cell-control-height !important;
            max-width: $cell-control-height !important;
        }

        .button-icon {
            width: 16px;
            height: 16px;
            min-width: 16px;
        }
    }
    border-radius: $radius-small;
    box-sizing: border-box;
    color: $color-white-text;
    font-size: $font-subtitle;
    white-space: nowrap;

    &.icon-only {
        width: $control-height !important;
        min-width: $control-height !important;
        max-width: $control-height !important;
        aspect-ratio: 1 / 1;
        padding: 0 !important;
    }

    &.full-width {
        width: 100% !important;
        flex: 1 1 0;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;

        &:hover {
            transform: none;
            opacity: 0.4;
        }
    }
}

.color-primary { background-color: $color-primary-blue; }
.color-danger { background-color: $color-pastel-red; }
.color-success { background-color: $color-pastel-green; }
.color-warning { background-color: $color-warning; }
.color-accent { background-color: $color-pastel-3; }
.color-teal { background-color: $color-pastel-4; }
.color-muted { background-color: $color-secondary-text; }

.is-secondary {
    background-color: $color-input-back;
    color: $color-white-text;
}

.tone-primary { color: $color-primary-blue; }
.tone-danger { color: $color-pastel-red; }
.tone-success { color: $color-pastel-green; }
.tone-warning { color: $color-warning; }
.tone-muted { color: $color-secondary-text; }

.button-inner {
    display: grid;
    grid-template: 1fr / 1fr;
    place-items: center;
    height: 100%;
    color: inherit;
}

.button-inner > * {
    grid-column: 1 / 1;
    grid-row: 1 / 1;
}

.button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: inherit;
}

.button-icon {
    width: 18px;
    height: 18px;
    min-width: 18px;
    stroke: currentColor;
    fill: none;

    &.filled {
        fill: currentColor;
        stroke: none;
    }
}

.button-label {
    color: inherit;
    font-size: inherit;
}

.button-loader {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60%;
    max-width: 28px;
}

.invisible {
    opacity: 0;
}

@include mobile {
    .design-button.hide-label-mobile {
        width: $control-height !important;
        min-width: $control-height !important;
        max-width: $control-height !important;
        aspect-ratio: 1 / 1;
        padding: 0 !important;

        &.size-cell {
            width: $cell-control-height !important;
            min-width: $cell-control-height !important;
            max-width: $cell-control-height !important;
        }

        &.size-small {
            width: 26px !important;
            min-width: 26px !important;
            max-width: 26px !important;
        }

        .button-label {
            display: none;
        }
    }
}
</style>
